import React, { useEffect, useState, useCallback } from "react";
import { App as AntApp, Button, Breadcrumb } from "antd";
import { PlusOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { BasicModal } from "../../../Shared";
import { ListQuiz } from "../ListQuiz/ListQuiz";
import { QuizForm } from "../QuizForm/QuizForm";
import { useTeacherEvalDashboard } from "./useTeacherEvalDashboard";
import { DashboardHome } from "./DashboardHome";
import { ClassStudents } from "./ClassStudents";
import { StudentDetail } from "./StudentDetail";
import { RankingModal } from "./RankingModal";
import styles from "./TeacherEvalDashboard.module.scss";

function TeacherEvalDashboardInner() {
  const { t } = useTranslation("evaluations");
  const {
    classNumbers,
    loadingClasses,
    quizzes,
    summary,
    loadingSummary,
    loadClassSummary,
    studentExams,
    loadingExams,
    loadStudentExams,
    loadRanking,
  } = useTeacherEvalDashboard();

  // view: home | exams | students | studentDetail
  const [view, setView] = useState("home");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);

  // creacion de examen (apartado Examenes)
  const [showCreate, setShowCreate] = useState(false);
  const [reload, setReload] = useState(false);
  const onReload = () => setReload((p) => !p);

  // ranking modal
  const [ranking, setRanking] = useState({
    open: false,
    quiz: null,
    classnumber: "",
  });

  // Seleccionar primera clase al cargar
  useEffect(() => {
    if (!selectedClass && classNumbers.length > 0) {
      setSelectedClass(classNumbers[0]);
      loadClassSummary(classNumbers[0]);
    }
  }, [classNumbers, selectedClass, loadClassSummary]);

  const onSelectClass = useCallback(
    (cls) => {
      setSelectedClass(cls);
      loadClassSummary(cls);
    },
    [loadClassSummary],
  );

  const onOpenStudent = useCallback(
    (record) => {
      setSelectedStudent(record);
      loadStudentExams(record.studentId);
      setView("studentDetail");
    },
    [loadStudentExams],
  );

  const onOpenRanking = useCallback(
    (exam) => {
      setRanking({
        open: true,
        quiz: { _id: exam.teacherQuizId, name: exam.name },
        classnumber: selectedClass,
      });
    },
    [selectedClass],
  );

  const closeRanking = useCallback(
    () => setRanking((r) => ({ ...r, open: false })),
    [],
  );

  // ----- Render por vista -----
  if (view === "exams") {
    return (
      <div className={styles.wrap}>
        <div className={styles.breadcrumbRow}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => setView("home")}
          />
          <Breadcrumb
            items={[
              { title: t("dashboard.bcEvaluaciones") },
              { title: t("dashboard.bcExams") },
            ]}
          />
          <Button
            className={styles.btnGreen}
            icon={<PlusOutlined />}
            style={{ marginLeft: "auto" }}
            onClick={() => setShowCreate(true)}
          >
            {t("dashboard.newEval")}
          </Button>
        </div>

        <ListQuiz reload={reload} onReload={onReload} />

        <BasicModal
          show={showCreate}
          close={() => setShowCreate(false)}
          title={t("dashboard.createEval")}
          size="large"
        >
          <QuizForm onClose={() => setShowCreate(false)} onReload={onReload} />
        </BasicModal>
      </div>
    );
  }

  if (view === "students") {
    return (
      <div className={styles.wrap}>
        <div className={styles.breadcrumbRow}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => setView("home")}
          />
          <Breadcrumb
            items={[
              { title: t("dashboard.bcEvaluaciones") },
              { title: t("dashboard.bcStudents") },
            ]}
          />
        </div>

        <ClassStudents
          classNumbers={classNumbers}
          selectedClass={selectedClass}
          onSelectClass={onSelectClass}
          summary={summary}
          loadingSummary={loadingSummary}
          onOpenStudent={onOpenStudent}
        />

        <RankingModal
          open={ranking.open}
          onClose={closeRanking}
          quiz={ranking.quiz}
          classnumber={ranking.classnumber}
          loadRanking={loadRanking}
        />
      </div>
    );
  }

  if (view === "studentDetail") {
    return (
      <div className={styles.wrap}>
        <div className={styles.breadcrumbRow}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => setView("students")}
          />
          <Breadcrumb
            items={[
              { title: t("dashboard.bcStudents") },
              {
                title: selectedStudent
                  ? `${selectedStudent.name} ${selectedStudent.surname || ""}`
                  : t("dashboard.bcDetail"),
              },
            ]}
          />
        </div>

        <StudentDetail
          data={studentExams}
          loading={loadingExams}
          onOpenRanking={onOpenRanking}
        />

        <RankingModal
          open={ranking.open}
          onClose={closeRanking}
          quiz={ranking.quiz}
          classnumber={ranking.classnumber}
          loadRanking={loadRanking}
        />
      </div>
    );
  }

  // view === "home"
  return (
    <DashboardHome
      classNumbers={classNumbers}
      selectedClass={selectedClass}
      onSelectClass={onSelectClass}
      summary={summary}
      loadingSummary={loadingSummary || loadingClasses}
      quizzes={quizzes}
      onGoExams={() => setView("exams")}
      onGoStudents={() => setView("students")}
    />
  );
}

export function TeacherEvalDashboard() {
  return (
    <AntApp>
      <TeacherEvalDashboardInner />
    </AntApp>
  );
}
